import { quantidadeLinhasTelefone } from '@/lib/quantidadeLinhasTelefone';
import { produtoExigeAtivacaoChip, rotuloTipoChip, produtoTemChip } from '@/lib/tipoChipAtivacao';
import React, { useMemo, useState, useCallback, useRef, useEffect, Fragment } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Accordion } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit3, XCircle, Trash2, Loader2, Calendar, MapPin , Smartphone, Users, Building2, CalendarCheck, PenLine, ChevronDown, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toBrasilia } from '@/lib/dateUtils';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUpdateItemPedido } from '@/hooks/useUpdateItemPedido';
import { useObrigatoriedadesItem } from '@/hooks/useObrigatoriedadesItem';
import { useItemCompleto } from '@/hooks/useItemCompleto';
import { StatusBadgeInteractive } from '../shared/StatusBadgeInteractive';
import { ItemBadge } from '../shared/ItemBadge';
import { GestorCessionarioBadge } from '../shared/GestorCessionarioBadge';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { ProgressoBadge } from './ProgressoBadge';
import { SecaoObrigatoria } from './SecaoObrigatoria';
import { CampoObrigatorioInline } from './CampoObrigatorioInline';
import { AgendaTurnoInline } from './AgendaTurnoInline';
import { AgendaFixaTravada } from './AgendaFixaTravada';
import { agendaInstalacaoTexto, ehInstalacao, partesEmSaoPaulo } from '@/lib/turnoInstalacao';
import { useItemGestorCessionario } from '@/hooks/useItemGestorCessionario';
import { GestorField } from '../fields/GestorField';
import { CessionarioField } from '../fields/CessionarioField';
import { MultiLinhasField } from '../fields/MultiLinhasField';
import { normalizarOperadora } from '@/utils/operadoraNormalizer';
import { useGestorCessionarioList } from '@/hooks/useGestorCessionarioList';
import { GestorCessionarioEditDialog } from '@/components/carteira/GestorCessionarioEditDialog';
import { CancelamentoRecuperacaoCard } from '../CancelamentoRecuperacaoCard';
import { EnderecoInstalacaoInline } from './EnderecoInstalacaoInline';
import { ResumoObrigatorios } from './ResumoObrigatorios';
import { BotaoAcaoBKO } from '@/components/pedido/BotaoAcaoBKO';
import { usePendingSavesContext } from '@/contexts/PendingSavesContext';
import { cn } from '@/lib/utils';

interface ItemDetalhadoInlineProps {
  item: any;
  pedido: any;
  todosItens?: any[];
  onEditItem?: (item: any) => void;
  onDeleteItem?: (itemId: string) => void;
  onCancelarItem?: (item: any) => void;
  isGestor?: boolean;
  compact?: boolean;
}

export const ItemDetalhadoInline = React.memo(function ItemDetalhadoInline({
  item,
  pedido,
  todosItens = [],
  onEditItem,
  onDeleteItem,
  onCancelarItem,
  isGestor = false,
  compact = false,
}: ItemDetalhadoInlineProps) {
  // 🔄 BUSCAR DADOS FRESCOS DO BANCO usando useItemCompleto
  const { data: itemCompleto } = useItemCompleto(item?.id);
  const itemAtual = itemCompleto || item;

  console.log('🔍 [ItemDetalhadoInline] Props recebidas:', {
    itemId: item?.id,
    itemCompletoId: itemCompleto?.id,
    quadro_atual_prop: item?.quadro_atual,
    quadro_atual_completo: itemCompleto?.quadro_atual,
    coluna_atual_prop: item?.coluna_atual,
    coluna_atual_completo: itemCompleto?.coluna_atual,
    status_nome_prop: item?.status_nome,
    status_nome_completo: itemCompleto?.status_nome,
    numero_pedido_completo_prop: item?.numero_pedido_completo,
    numero_pedido_completo_completo: itemCompleto?.numero_pedido_completo,
    keys_prop: Object.keys(item || {}),
    keys_completo: Object.keys(itemCompleto || {})
  });

  console.log('🔍 [ItemDetalhadoInline] Valores de data:', {
    itemId: item?.id,
    data_ativacao_prop: item?.data_ativacao,
    data_ativacao_completo: itemCompleto?.data_ativacao,
    data_ativacao_atual: itemAtual.data_ativacao,
    mes_trabalho_prop: item?.mes_trabalho,
    mes_trabalho_completo: itemCompleto?.mes_trabalho,
    mes_trabalho_atual: itemAtual.mes_trabalho,
    data_agendamento_prop: item?.data_agendamento,
    data_agendamento_completo: itemCompleto?.data_agendamento,
    data_agendamento_atual: itemAtual.data_agendamento,
    tipo_data_ativacao: typeof itemAtual.data_ativacao,
    tipo_mes_trabalho: typeof itemAtual.mes_trabalho
  });

  const updateItemMutation = useUpdateItemPedido();
  const queryClient = useQueryClient();
  const { gestor, cessionario } = useItemGestorCessionario(
    itemAtual?.gestor_id, 
    itemAtual?.cessionario_id,
    pedido?.cliente_id  // Para fallback de CPF/RG
  );
  const [isSavingLinhas, setIsSavingLinhas] = useState(false);
  const [editandoGestor, setEditandoGestor] = useState(false);
  const [editandoCessionario, setEditandoCessionario] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // Estado para controlar expansão dos detalhes
  
  // ✅ Contexto de salvamentos pendentes (fila atômica)
  const pendingSaves = usePendingSavesContext();
  
  // Estado local para endereço de instalação (evitar travamento de input)
  const [enderecoLocal, setEnderecoLocal] = useState<any>(null);
  
  // ✅ AJUSTE 2: Tipagem correta para browser (ReturnType ao invés de NodeJS.Timeout)
  const enderecoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Hook para editar gestor e cessionário
  const { updateGestor, updateCessionario } = useGestorCessionarioList(pedido?.cliente_id);
  
  // Sincronizar endereço local com dados do banco
  useEffect(() => {
    if (itemCompleto?.endereco_instalacao) {
      setEnderecoLocal(itemCompleto.endereco_instalacao);
    }
  }, [itemCompleto?.endereco_instalacao]);

  // ✅ AJUSTE 1: Cleanup NÃO tenta salvar - apenas cancela timer
  // O guard do PainelPedido cuida do flush ao fechar
  useEffect(() => {
    return () => {
      if (enderecoDebounceRef.current) {
        clearTimeout(enderecoDebounceRef.current);
        enderecoDebounceRef.current = null;
        console.log('⚠️ [ItemDetalhadoInline] Timer de endereço cancelado no unmount');
      }
    };
  }, []);

  // ✅ Handler onBlur: Força flush imediato do endereço (cancela debounce)
  const handleEnderecoBlur = useCallback(() => {
    if (enderecoDebounceRef.current) {
      clearTimeout(enderecoDebounceRef.current);
      enderecoDebounceRef.current = null;
      
      // Forçar enqueue imediato se tiver valor local
      if (enderecoLocal) {
        console.log('📍 [onBlur] Forçando salvamento imediato do endereço');
        pendingSaves.enqueue(itemAtual.id, {
          endereco_instalacao: enderecoLocal
        }).catch(err => {
          console.error('❌ Erro ao salvar endereço no blur:', err);
        });
      }
    }
  }, [pendingSaves, itemAtual.id, enderecoLocal]);

  // Monitorar mudanças nas linhas telefônicas
  useEffect(() => {
    const linhasAtuais = itemCompleto?.linhas_telefone || [];
    console.log('📞 [useEffect] Linhas telefônicas atualizadas:', {
      itemId: itemAtual.id,
      linhas: linhasAtuais,
      timestamp: new Date().toISOString()
    });
  }, [itemCompleto?.linhas_telefone, itemAtual.id]);

  // Buscar obrigatoriedades do produto
  const { data: obrigatoriedades } = useObrigatoriedadesItem(
    itemAtual?.produto_id,
    'negociacao',
    true // Buscar todas independente do contexto
  );

  // Extrair valores com proteção robusta contra Proxy objects
  const extractValue = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val.trim();
    
    // Se é objeto com .value (Supabase Proxy)
    if (typeof val === 'object' && val !== null && 'value' in val) {
      return String(val.value || '').trim();
    }
    
    // Tentar serializar
    try {
      const serialized = JSON.parse(JSON.stringify(val));
      if (typeof serialized === 'string') return serialized.trim();
      if (typeof serialized === 'object' && 'value' in serialized) {
        return String(serialized.value || '').trim();
      }
      const str = String(serialized).trim();
      if (str === '[object Object]') return '';
      return str;
    } catch {
      return '';
    }
  };

  // ✅ USAR itemAtual (dados frescos do banco) ao invés de item (props)
  const quadroAtual = extractValue(itemAtual.quadro_atual) || '';
  const colunaAtual = extractValue(itemAtual.coluna_atual) || '';
  const temSnapshotRenovacao = Array.isArray(itemAtual.linhas_telefone)
    && itemAtual.linhas_telefone.some((linha: unknown) => {
      if (!linha || typeof linha !== 'object') return false;
      const dados = linha as Record<string, unknown>;
      return Boolean(dados.delta_formula_sign) || Object.prototype.hasOwnProperty.call(dados, 'delta_economico');
    });

  // 🔒 Bloquear edição de "Dados da Linha" quando item saiu do COMERCIAL
  const dadosLinhaBloqueados = temSnapshotRenovacao || (quadroAtual !== '' && quadroAtual !== 'COMERCIAL');
  const statusNome = extractValue(itemAtual.status_nome) || '';
  const statusCor = extractValue(itemAtual.status_cor) || '#808080';

  // ✅ GARANTIR que numero_pedido_completo SEMPRE seja string válida
  const numeroPedidoCompleto = extractValue(itemAtual.numero_pedido_completo) || 
    `#P${itemAtual.item_seq || itemAtual.seq || '?'}`;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Ref para armazenar timeouts de debounce
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  
  // ✅ Ref para guardar valores pendentes (para flush no unmount)
  const pendingFieldUpdates = useRef<Record<string, any>>({});

  // ✅ Flush de todos os debounce pendentes no unmount
  useEffect(() => {
    return () => {
      // Cancelar todos os timers
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
      debounceTimers.current = {};
      
      // Salvar todos os valores pendentes via PendingSaves (fila atômica)
      const pending = { ...pendingFieldUpdates.current };
      pendingFieldUpdates.current = {};
      
      if (Object.keys(pending).length > 0) {
        console.log('⚠️ [ItemDetalhadoInline] Flush no unmount - salvando campos pendentes:', pending);
        pendingSaves.enqueue(itemAtual.id, pending).catch(err => {
          console.error('❌ Erro ao flush pendentes no unmount:', err);
        });
      }
    };
  }, [pendingSaves, itemAtual.id]);

  // Mapeamento de campos técnicos para nomes amigáveis na timeline
  const CAMPO_TIMELINE_MAP: Record<string, string> = {
    numero_simulacao_operadora: 'HP',
    numero_cotacao_operadora: 'Cotação',
    numero_pedido_operadora: 'Proposta',
  };

  // Campos de agenda usam tipo 'agenda' na timeline
  const AGENDA_TIMELINE_MAP: Record<string, string> = {
    agenda_tipo: 'Tipo',
    agenda_data: 'Data',
  };

  // Update imediato (sem debounce) - usar para selects e campos especiais
  const handleUpdateImmediate = useCallback(async (field: string, value: any, retries = 5) => {
    // ✅ Limpar pending se estiver fazendo save imediato deste campo
    delete pendingFieldUpdates.current[field];
    if (debounceTimers.current[field]) {
      clearTimeout(debounceTimers.current[field]);
      delete debounceTimers.current[field];
    }
    
    // Capturar valor anterior ANTES da atualização (para timeline)
    const valorAnterior = itemAtual[field] || '';
    
    console.log(`🚀 [handleUpdateImmediate] INICIADO:`, {
      field,
      value,
      itemId: itemAtual.id,
      timestamp: new Date().toISOString()
    });
    
    let attempt = 0;
    while (attempt < retries) {
      try {
        console.log(`🔄 [RETRY ${attempt + 1}/${retries}] Tentando salvar ${field}...`, {
          field,
          value,
          itemId: itemAtual.id
        });
        
        await updateItemMutation.mutateAsync({
          itemId: itemAtual.id,
          updates: { [field]: value }
        });
        
        console.log(`✅ [RETRY ${attempt + 1}/${retries}] ${field} salvo com sucesso!`, {
          field,
          value,
          valorSalvo: value
        });

        // ✅ Registrar movimento na timeline para campos HP, Cotação, Proposta
        const campoTimeline = CAMPO_TIMELINE_MAP[field];
        const campoAgenda = AGENDA_TIMELINE_MAP[field];
        
        if (campoTimeline && String(value || '') !== String(valorAnterior || '')) {
          try {
            const { data: { user } } = await supabase.auth.getUser();
            await supabase.from('movimento').insert({
              entidade: 'pedido',
              entidade_id: pedido?.id || itemAtual.pedido_id,
              tipo: 'campo',
              campo: campoTimeline,
              de: valorAnterior || null,
              para: value || null,
              cnpj: pedido?.cnpj || null,
              usuario_id: user?.id || null,
              descricao: `${campoTimeline}: ${valorAnterior || '(vazio)'} → ${value || '(vazio)'}`,
            });
            console.log(`📝 [Timeline] Movimento registrado: ${campoTimeline}`);
            queryClient.invalidateQueries({ queryKey: ['timeline-unificada'] });
          } catch (movErr) {
            console.error('⚠️ Erro ao registrar movimento na timeline:', movErr);
          }
        }

        // ✅ Registrar movimento na timeline para campos de Agenda
        if (campoAgenda && String(value || '') !== String(valorAnterior || '')) {
          try {
            const { data: { user } } = await supabase.auth.getUser();
            await supabase.from('movimento').insert({
              entidade: 'pedido',
              entidade_id: pedido?.id || itemAtual.pedido_id,
              tipo: 'agenda',
              campo: campoAgenda,
              de: valorAnterior || null,
              para: value || null,
              cnpj: pedido?.cnpj || null,
              usuario_id: user?.id || null,
              descricao: `[AGENDA] ${campoAgenda}: ${valorAnterior || '(vazio)'} → ${value || '(vazio)'}`,
            });
            console.log(`📝 [Timeline] Movimento agenda registrado: ${campoAgenda}`);
            queryClient.invalidateQueries({ queryKey: ['timeline-unificada'] });
          } catch (movErr) {
            console.error('⚠️ Erro ao registrar movimento agenda na timeline:', movErr);
          }
        }

        return; //  Sucesso, sair do loop
      } catch (error: any) {
        attempt++;
        console.error(`❌ Tentativa ${attempt}/${retries} falhou:`, {
          field,
          error: error.message,
          code: error.code
        });
        
        // Verificar se é erro de rede/502
        const isNetworkError = 
          error.code === '502' || 
          error.message?.includes('502') || 
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('Bad Gateway');
        
        if (attempt < retries && isNetworkError) {
          // Delays progressivos: 1s, 2s, 4s, 8s
          const delay = Math.pow(2, attempt) * 1000;
          console.log(`⏳ Erro de rede detectado. Aguardando ${delay}ms antes de tentar novamente...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          console.error(`💀 [FATAL] Esgotadas ${retries} tentativas ou erro não-retryable`);
          throw error;
        }
      }
    }
  }, [itemAtual.id, itemAtual, updateItemMutation, pedido, queryClient]);

  // Update com debounce (800ms) - usar para inputs de texto
  // ✅ Agora também guarda valor pendente para flush no unmount
  const handleUpdate = useCallback((field: string, value: any) => {
    // Guardar valor pendente
    pendingFieldUpdates.current[field] = value;
    
    // Limpar timer anterior deste campo específico
    if (debounceTimers.current[field]) {
      clearTimeout(debounceTimers.current[field]);
    }

    // Criar novo timer
    debounceTimers.current[field] = setTimeout(async () => {
      // Limpar pending ao executar
      delete pendingFieldUpdates.current[field];
      delete debounceTimers.current[field];
      
      try {
        await updateItemMutation.mutateAsync({
          itemId: itemAtual.id,
          updates: { [field]: value }
        });
        console.log(`✅ [debounce] ${field} salvo com sucesso`);
      } catch (error) {
        console.error('Erro ao atualizar campo:', error);
      }
    }, 800); // 800ms de debounce
  }, [itemAtual.id, updateItemMutation]);

  // Calcular campos obrigatórios e preenchidos
  const { camposObrigatorios, camposPreenchidos } = useMemo(() => {
    const obrigatoriosList: string[] = [];
    const preenchidosList: string[] = [];

    // ✅ Detectar tipo de produto para ajustar validação
    const produtoNome = itemAtual.produto_nome || itemAtual.plano_nome || '';
    const nomeNormalizado = produtoNome.toUpperCase();
    const isTV = nomeNormalizado.includes('TV');
    const isFIBRA = nomeNormalizado.includes('FIBRA'); //  Declarar DENTRO do useMemo para evitar TDZ
    
    // ✅ Produtos add-on (PASSAPORT, etc.) não exigem CPF/RG do gestor
    const isAddOn = nomeNormalizado.includes('PASSAPORT') || 
                    nomeNormalizado.includes('ADD-ON') ||
                    nomeNormalizado.includes('ADDON');

    // TT e renovação não têm chip: a linha já é do cliente e continua com o
    // mesmo número e o mesmo chip (Douglas 22/09/2026). O banco apaga o campo,
    // então cobrá-lo aqui deixava o item eternamente "incompleto".
    const temChipProduto = produtoTemChip(itemAtual.produto_nome || itemAtual.produto?.nome);

    const validarCampo = (nome: string, valor: any) => {
      obrigatoriosList.push(nome);
      if (valor !== null && valor !== undefined && valor !== '') {
        preenchidosList.push(nome);
      }
    };

    // Validar baseado nas obrigatoriedades do produto
    if (obrigatoriedades && Array.isArray(obrigatoriedades)) {
      obrigatoriedades.forEach((config: any) => {
        const codigo = config.obrigatoriedades_catalogo?.codigo || config.campo_codigo;
        const nomeCampo = config.obrigatoriedades_catalogo?.nome || config.campo_label || codigo;

        switch (codigo) {
          case 'gestor_cliente':
          case 'gestor_id':
            // ✅ PRIORIZAR dados do hook (gestor) sobre itemAtual (view) para evitar stale cache
            const cpfGestor = gestor?.cpf ?? itemAtual.gestor_cpf;
            const rgGestor = gestor?.rg ?? itemAtual.gestor_rg;
            
            // ✅ Add-ons: gestor completo = ter ID selecionado (não precisa CPF/RG)
            // Outros produtos: ID + CPF + RG
            const gestorCompleto = isAddOn 
              ? !!itemAtual.gestor_id 
              : (itemAtual.gestor_id && cpfGestor && rgGestor);
            
            obrigatoriosList.push('Gestor');
            if (gestorCompleto) {
              preenchidosList.push('Gestor');
            }
            
            // Adicionar indicadores específicos de campos faltantes (apenas para não add-ons)
            if (!isAddOn && itemAtual.gestor_id) {
              if (!cpfGestor) {
                obrigatoriosList.push('CPF do Gestor');
              } else {
                preenchidosList.push('CPF do Gestor');
              }
              if (!rgGestor) {
                obrigatoriosList.push('RG do Gestor');
              } else {
                preenchidosList.push('RG do Gestor');
              }
            }
            break;
          case 'cessionario':
            validarCampo('Cessionário', itemAtual.cessionario_id);
            break;
          case 'contrato_pf_tt':
            validarCampo('Contrato PF da fibra', String(itemAtual.tt_contrato_pf ?? '').trim());
            break;
          case 'produto':
          case 'produto_id':
            validarCampo('Produto', itemAtual.produto_id || itemAtual.plano_nome);
            break;
          case 'plano_nome':
            validarCampo('Plano', itemAtual.plano_nome);
            break;
          case 'plano_valor':
            validarCampo('Valor do Plano', itemAtual.plano_valor);
            break;
          case 'quantidade':
            if (itemAtual.quantidade && itemAtual.quantidade >= 1) {
              obrigatoriosList.push('Quantidade');
              preenchidosList.push('Quantidade');
            } else {
              validarCampo('Quantidade', itemAtual.quantidade);
            }
            break;
          case 'valor_unitario':
            validarCampo('Valor Unitário', itemAtual.valor_unit);
            break;
          case 'valor_total':
            // Calcular subtotal como quantidade × valor_unit
            const subtotal = (itemAtual.quantidade || 0) * (itemAtual.valor_unit || 0);
            validarCampo('Valor Total', subtotal > 0 ? subtotal : null);
            break;
          case 'ddd':
          case 'linha':
          case 'ddd_linha':
          case 'tipo_chip':
          case 'operadora_doadora':
            // ✅ TV não precisa de campos de linha telefônica
            if (isTV) {
              break;
            }
            // ✅ ADD-ONS (PASSAPORT, etc.) não precisam de campos de linha
            // São serviços extras vinculados a linhas já existentes
            if (isAddOn) {
              break;
            }
            if (temSnapshotRenovacao && (codigo === 'tipo_chip' || codigo === 'operadora_doadora')) {
              break;
            }
            if (!temChipProduto && codigo === 'tipo_chip') {
              break;
            }
            // ✅ FIBRA com "sem linha fixa" ativo: pular validação desses campos
            const isFibraSemLinha = isFIBRA && itemAtual.sem_linha_fixa === true;
            if (isFibraSemLinha) {
              break;
            }
            
            // ✅ Caso específico: operadora_doadora tem validação simples
            if (codigo === 'operadora_doadora') {
              validarCampo('Operadora Doadora', itemAtual.operadora_doadora);
              break;
            }
            
            const linhas = itemAtual.linhas_telefone || [];
            const quantidadeEsperada = quantidadeLinhasTelefone(itemAtual.quantidade || 1, linhas, isFIBRA);
            
            // Função auxiliar: verificar se UMA linha está completa
            const linhaEstaCompleta = (linha: any) => {
              const dddValido = linha.ddd && linha.ddd.length === 2;
              
              // Para produtos HA, "Número Novo" é válido
              const isNumeroNovo = linha.linha?.toUpperCase() === 'NÚMERO NOVO';
              const linhaValida = isNumeroNovo || (linha.linha && linha.linha.length >= 8 && linha.linha.length <= 9);
              
              const tipoChipValido = temSnapshotRenovacao || !temChipProduto || (linha.tipo_chip && linha.tipo_chip !== '');
              return dddValido && linhaValida && tipoChipValido;
            };
            
            // 🔍 LOG: Estado das linhas ANTES da validação
            console.log('🔍 [VALIDAÇÃO LINHAS] Estado:', {
              quantidadeEsperada,
              quantidadeAtual: linhas.length,
              sem_linha_fixa: itemAtual.sem_linha_fixa,
              linhas: linhas.map((l: any, idx: number) => ({
                index: idx + 1,
                ddd: l.ddd,
                ddd_valido: !!(l.ddd && l.ddd.length === 2),
                linha: l.linha,
                linha_valida: !!(l.linha && l.linha.length >= 8 && l.linha.length <= 9),
                tipo_chip: l.tipo_chip,
                tipo_chip_valido: !!(l.tipo_chip && l.tipo_chip !== ''),
                completa: linhaEstaCompleta(l)
              }))
            });
            
            // Verificar se TODAS as linhas esperadas estão completas
            const todasLinhasCompletas = linhas.length === quantidadeEsperada && 
              linhas.length > 0 &&
              linhas.every(linhaEstaCompleta);
            
            // Retrocompatibilidade: verificar campos antigos (para quantidade = 1)
            const temCamposAntigos = quantidadeEsperada === 1 &&
              itemAtual.ddd && itemAtual.ddd.length === 2 &&
              itemAtual.linha && itemAtual.linha.length >= 8 &&
              (temSnapshotRenovacao || !temChipProduto || (itemAtual.tipo_chip && itemAtual.tipo_chip !== ''));
            
            console.log('🔍 [VALIDAÇÃO LINHAS] Resultado:', {
              todasLinhasCompletas,
              temCamposAntigos,
              resultadoFinal: todasLinhasCompletas || temCamposAntigos
            });
            
            // Adicionar campos à lista de obrigatórios (sem duplicatas)
            if (!obrigatoriosList.includes('DDD')) obrigatoriosList.push('DDD');
            if (!obrigatoriosList.includes('Linhas Telefônicas')) obrigatoriosList.push('Linhas Telefônicas');
            if (!temSnapshotRenovacao && temChipProduto && !obrigatoriosList.includes('Tipo de Chip')) obrigatoriosList.push('Tipo de Chip');
            
            // Marcar como preenchido APENAS se TODAS as linhas estão completas
            if (todasLinhasCompletas || temCamposAntigos) {
              if (!preenchidosList.includes('DDD')) preenchidosList.push('DDD');
              if (!preenchidosList.includes('Linhas Telefônicas')) preenchidosList.push('Linhas Telefônicas');
              if (!temSnapshotRenovacao && temChipProduto && !preenchidosList.includes('Tipo de Chip')) preenchidosList.push('Tipo de Chip');
            }
            
            break;
          case 'endereco_instalacao':
            // Usar endereço local (otimista) ou do banco
            const endereco = enderecoLocal || itemAtual.endereco_instalacao;
            const enderecoCompleto = endereco && 
              endereco.cep &&
              endereco.logradouro &&
              endereco.numero &&
              endereco.bairro &&
              endereco.cidade &&
              endereco.uf;
            
            obrigatoriosList.push('Endereço de Instalação');
            if (enderecoCompleto) {
              preenchidosList.push('Endereço de Instalação');
            }
            break;
          case 'detalhes_pedido':
          case 'observacoes_item':
            // ✅ Mínimo 10 caracteres para fechamento
            const obsValida = itemAtual.observacoes_item && 
                              itemAtual.observacoes_item.trim().length >= 10;
            obrigatoriosList.push('Observações');
            if (obsValida) {
              preenchidosList.push('Observações');
            }
            break;
          case 'data_agendamento':
            validarCampo('Data Agendamento', itemAtual.data_agendamento);
            break;
          case 'agenda_tipo':
            validarCampo('Tipo de Agenda', itemAtual.agenda_tipo);
            break;
          default:
            validarCampo(nomeCampo, itemAtual[codigo]);
        }
      });
    } else {
      // ⚠️ FALLBACK: Produto sem grupo de obrigatoriedades configurado
      console.warn('⚠️ [VALIDAÇÃO] Produto sem grupo de obrigatoriedades, usando fallback:', {
        produtoId: itemAtual.produto_id,
        produtoNome: itemAtual.produto_nome,
        isAddOn
      });

      // ✅ Add-ons: apenas gestor selecionado + valor + quantidade
      // Outros produtos: Gestor com CPF/RG + valor + quantidade
      const cpfGestor = gestor?.cpf ?? itemAtual.gestor_cpf;
      const rgGestor = gestor?.rg ?? itemAtual.gestor_rg;
      const gestorCompleto = isAddOn 
        ? !!itemAtual.gestor_id 
        : (itemAtual.gestor_id && cpfGestor && rgGestor);
      
      obrigatoriosList.push('Gestor');
      if (gestorCompleto) {
        preenchidosList.push('Gestor');
      }
      
      // CPF/RG só são obrigatórios para produtos que não são add-on
      if (!isAddOn && itemAtual.gestor_id) {
        if (!cpfGestor) {
          obrigatoriosList.push('CPF do Gestor');
        } else {
          preenchidosList.push('CPF do Gestor');
        }
        if (!rgGestor) {
          obrigatoriosList.push('RG do Gestor');
        } else {
          preenchidosList.push('RG do Gestor');
        }
      }
      
      // Validar Valor Unitário
      obrigatoriosList.push('Valor Unitário');
      if (itemAtual.valor_unit && itemAtual.valor_unit > 0) {
        preenchidosList.push('Valor Unitário');
      }

      // Validar Quantidade
      obrigatoriosList.push('Quantidade');
      if (itemAtual.quantidade && itemAtual.quantidade >= 1) {
        preenchidosList.push('Quantidade');
      }
    }

    // 🔍 LOG DE DEBUG: Estado da validação
    console.log('🔍 [VALIDAÇÃO] Estado dos campos:', {
      produtoNome: itemAtual.produto_nome,
      quantidade: itemAtual.quantidade,
      camposObrigatorios: obrigatoriosList,
      camposPreenchidos: preenchidosList,
      linhas_telefone: itemAtual.linhas_telefone,
      ddd_antigo: itemAtual.ddd,
      linha_antiga: itemAtual.linha,
      tipo_chip_antigo: itemAtual.tipo_chip,
      operadora_doadora: itemAtual.operadora_doadora
    });

    // 🔍 LOG DETALHADO: Estado de cada linha
    if (itemAtual.linhas_telefone && itemAtual.linhas_telefone.length > 0) {
      itemAtual.linhas_telefone.forEach((linha: any, idx: number) => {
        console.log(`📞 [LINHA ${idx + 1}] Estado:`, {
          ddd: linha.ddd,
          ddd_valido: linha.ddd?.length === 2,
          linha: linha.linha,
          linha_valida: linha.linha?.length >= 8,
          tipo_chip: linha.tipo_chip,
          tipo_chip_valido: !!linha.tipo_chip,
          operadora: linha.operadora_doadora,
          linha_completa: !!(linha.ddd?.length === 2 && linha.linha?.length >= 8 && linha.tipo_chip)
        });
      });
    }

    // ✅ DEDUPLICAR listas para evitar duplicatas e cálculo incorreto (ex: 125%)
    const obrigatoriosUnicos = Array.from(new Set(obrigatoriosList));
    const preenchidosUnicos = Array.from(new Set(preenchidosList));
    
    // ✅ Garantir que preenchidos só conta itens que estão em obrigatórios
    const obrigatoriosSet = new Set(obrigatoriosUnicos);
    const preenchidosValidos = preenchidosUnicos.filter(c => obrigatoriosSet.has(c));

    return { camposObrigatorios: obrigatoriosUnicos, camposPreenchidos: preenchidosValidos };
  }, [itemAtual, obrigatoriedades, gestor?.cpf, gestor?.rg, enderecoLocal, temSnapshotRenovacao]);

  // Determinar seções expandidas por padrão (as que tem campos faltando)
  const defaultExpandedSections = useMemo(() => {
    const sections: string[] = [];
    const camposFaltando = camposObrigatorios.filter(c => !camposPreenchidos.includes(c));
    
    if (camposFaltando.length > 0) {
      // Expandir seções com campos faltando
      if (camposFaltando.some(c => ['DDD', 'Linha', 'Operadora Doadora', 'Plano', 'Valor do Plano'].includes(c))) {
        sections.push('dados-linha');
      }
      if (camposFaltando.some(c => ['Endereço de Instalação'].includes(c))) {
        sections.push('endereco');
      }
      if (camposFaltando.some(c => ['Gestor', 'Cessionário'].includes(c))) {
        sections.push('responsaveis');
      }
      if (camposFaltando.some(c => ['DDD', 'Tipo de Chip', 'Observações'].includes(c))) {
        sections.push('observacoes');
      }
      if (camposFaltando.some(c => ['Data Agendamento'].includes(c))) {
        sections.push('datas');
      }
    }
    
    return sections;
  }, [camposObrigatorios, camposPreenchidos]);

    // 🔧 CORREÇÃO: Normalizar nome do produto com fallback robusto
    const nomeNormalizado = (itemAtual.produto_nome || itemAtual.produto?.nome || '')
      .toUpperCase()
      .replace(/\u00A0/g, ' ') // Substituir NBSP por espaço normal
      .trim();
    const familiaNorm = (itemAtual.familia || '').toString().toUpperCase().trim();

    // 🔧 CORREÇÃO: Flags individuais com word boundary (não depende de espaço após sigla)
    const isPN = /^PN\b/.test(nomeNormalizado);  // PN, PN PF PJ, PN PJ PJ
    const isPP = /^PP\b/.test(nomeNormalizado);  // PP MÓVEL 100GB, PP MÓVEL 15GB
    const isTT = /^TT\b/.test(nomeNormalizado);  // TT (Troca de Titularidade)
    const isMP = /^MP\b/.test(nomeNormalizado);  // MP MÓVEL, MP FIXA
    const isFIBRA = nomeNormalizado.includes('FIBRA');
    const isHA = nomeNormalizado.startsWith('HA'); // Habilitação
    const isTV = nomeNormalizado.includes('TV');

    // TT e renovação mantêm o chip do cliente: o campo Tipo vira "Sem chip".
    const temChipProduto = produtoTemChip(itemAtual.produto_nome || itemAtual.produto?.nome);

    // 🆕 Produtos cujo nome não casa com prefixos legados, mas exigem linha telefônica
    const isRENOVACAO = /RENOVA[ÇC][AÃ]O/.test(nomeNormalizado); // RENOVAÇAO, RENOVAÇÃO, RENOVACAO
    const isPASSAPORT = nomeNormalizado.startsWith('PASSAPORT'); // PASSAPORT AMERICA/EUROPA/MUNDO

    // 🆕 Família como fonte de verdade: qualquer Móvel/Fixa Básica precisa de linha
    // (exceto TV, tratado pelo gate !isTV abaixo). Isso evita que produtos novos
    // cadastrados sem prefixo conhecido (ex: RENOVAÇAO) percam a seção e travem o consultor.
    const familiaPrecisaLinha =
      familiaNorm === 'MÓVEL' || familiaNorm === 'MOVEL' ||
      familiaNorm === 'FIXA BÁSICA' || familiaNorm === 'FIXA BASICA';

    // 🆕 Fibra/instalação: a família já determina que a agenda é de instalação.
    // Usado para auto-preencher agenda_tipo ao salvar a data.
    const ehFibraInstalacao =
      familiaNorm.includes('FIBRA') ||
      familiaNorm === 'FIXA BÁSICA' || familiaNorm === 'FIXA BASICA' ||
      isFIBRA;

    // Produtos que precisam de campos de linha telefônica:
    // - Prefixos legados: PN, PP, TT, MP, HA
    // - Nomes específicos: RENOVAÇAO, PASSAPORT
    // - FIBRA (exceto se "sem linha fixa")
    // - Fallback por família: Móvel/Fixa Básica genéricos
    // - Sempre exclui TV
    const precisaLinhasTelefone = !isTV && (
      isPN || isPP || isTT || isMP || isHA || isRENOVACAO || isPASSAPORT ||
      (isFIBRA && itemAtual.sem_linha_fixa !== true) ||
      (familiaPrecisaLinha && !isFIBRA)
    );

    // Instalação é agendada por TURNO (Douglas 26/09/2026); portabilidade segue com hora.
    const agendaPorTurno = itemAtual.agenda_tipo === 'INSTALACAO' || (ehFibraInstalacao && itemAtual.agenda_tipo !== 'PORTABILIDADE');
    // A data da fixa se marca só no Inputar (Douglas 30/10/2026); aqui ela aparece travada.
    const agendaFixaTravada = ehFibraInstalacao && nomeNormalizado !== 'TT FIXA' && itemAtual.agenda_tipo !== 'PORTABILIDADE';
    const agendaEmSaoPaulo = partesEmSaoPaulo(itemAtual.data_agendamento);

  if (!pedido || !item) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Carregando dados...</span>
        </div>
      </Card>
    );
  }

  if (!item.id) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Item sem ID válido</AlertDescription>
      </Alert>
    );
  }

  // ✅ Calcular se está completo usando a mesma lógica de Sets
  const isCompleto = camposObrigatorios.length > 0 && 
    camposPreenchidos.length === camposObrigatorios.length;

  return (
    <Card className={cn(
      'overflow-hidden border bg-card transition-shadow',
      compact
        ? 'rounded-md border-primary/15 border-l-[3px] border-l-primary/65 bg-primary/[0.025] shadow-none'
        : 'rounded-lg border-border border-l-[3px] shadow-sm',
      !compact && (isCompleto ? 'border-l-primary' : 'border-l-primary'),
    )}>
      <CardHeader className={cn('space-y-1.5', compact ? 'p-2.5' : 'p-3 pb-2.5')}>
        {/* Header com número, status e ações */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
                <CopyOnClick value={String(numeroPedidoCompleto ?? '')} className="min-w-0 max-w-full">
                  <span className="truncate font-mono text-xs font-semibold text-foreground">
                    {numeroPedidoCompleto}
                  </span>
                </CopyOnClick>
            <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[9px]">{itemAtual.familia || 'N/A'}</Badge>
            {itemAtual.pedido_id && (quadroAtual || colunaAtual || statusNome) && (
              <StatusBadgeInteractive
                pedidoId={itemAtual.pedido_id}
                itemId={itemAtual.id}
                quadro={quadroAtual}
                coluna={colunaAtual}
                statusNome={statusNome}
                statusCor={statusCor}
                onStatusChange={() => {
                  console.log('🔄 [ItemDetalhadoInline] Status alterado, invalidando queries...');
                  queryClient.invalidateQueries({ queryKey: ['item-completo', itemAtual.id] });
                  queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', pedido?.id] });
                  queryClient.invalidateQueries({ queryKey: ['pedido-detail', pedido?.id] });
                }}
              />
            )}
          </div>
          
          {/* Botões de ação diretos */}
          <div className="flex shrink-0 items-center gap-0.5">
            {compact && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-[10px] text-muted-foreground"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                Detalhes
                <ChevronDown className={cn('h-3 w-3 transition-transform', isExpanded && 'rotate-180')} />
              </Button>
            )}
            {/* Editar Item */}
            {onEditItem && !temSnapshotRenovacao && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7"
                      onClick={() => onEditItem(itemAtual)}
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Editar item</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Cancelar Item — some em COMERCIAL: ali ainda é proposta, não virou
                venda, então não há pedido para cancelar. Ajuste = Revisar
                Proposta; negócio morto = "(0%) NEGOCIAÇÃO CANCELADA". (Douglas 04/08) */}
            {onCancelarItem && itemAtual.coluna_atual !== 'PEDIDO CANCELADO'
              && itemAtual.quadro_atual !== 'COMERCIAL' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => {
                        console.log('🔴 [CANCELAR] Botão clicado:', {
                          itemId: itemAtual.id,
                          itemNome: itemAtual.produto?.nome || 'N/A',
                          pedidoId: itemAtual.pedido_id,
                          pedidoNumero: numeroPedidoCompleto,
                          statusAtual: itemAtual.status_id,
                          statusNome: statusNome,
                          quadroAtual: quadroAtual,
                          colunaAtual: colunaAtual,
                          dataCancelamento: itemAtual.data_cancelamento,
                          timestamp: new Date().toISOString()
                        });
                        onCancelarItem(itemAtual);
                      }}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>PEDIDO CANCELADO</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Deletar Item */}
            {onDeleteItem && !temSnapshotRenovacao && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => {
                        if (confirm('Tem certeza que deseja remover este item permanentemente?')) {
                          onDeleteItem(itemAtual.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Deletar item permanentemente</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        {/* Linha com produto, valor e badge */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            {/* R2 (rodada 2): 16px -> 12.5/600. O painel estava ~70% acima da
                régua e este e' o nome que mais salta. */}
            <span className="min-w-0 truncate text-xs font-semibold text-foreground">
              {itemAtual.produto_nome || itemAtual.produto?.nome || 'Produto sem nome'}
              {itemAtual.plano_nome && ` - ${itemAtual.plano_nome}`}
            </span>
            <ItemBadge 
              familia={itemAtual.familia} 
              faturamentoTipo={itemAtual.faturamento_tipo}
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="font-display text-sm font-bold tabular-nums text-primary">
              {formatCurrency(itemAtual.subtotal)}
            </span>
            <span className="flex-shrink-0 text-[10px] text-muted-foreground">
              Qtd: {itemAtual.quantidade}
            </span>
          </div>
        </div>

        {/* O BLOCO DO BKO — é o desenho que o Douglas aprovou em 17/09/2026,
            e eu tinha entregue bem menos do que ele.
            Regras dele, na ordem em que foram ditas:
            · "o número está na frente aparecendo com possibilidade de copiar"
            · "gestor, CPF, e-mail dele. MESMO QUANDO FECHADO tem que ter isso"
            · "não é copiar tudo, é copiar uma a uma" → cada dado é um botão,
              sublinhado para mostrar que se clica; o "copiar tudo" fica ao lado
              como atalho, não como substituto
            · "os nomes não estão padrão um do outro, eles têm que estar todos
              no mesmo lugar" → GESTOR e CEDENTE são DUAS LINHAS rotuladas, o
              nome sempre na mesma coluna. Quando são a mesma pessoa vira UMA
              linha "GESTOR · TAMBÉM CEDENTE" — sem repetir o nome duas vezes.
            Fechado ele aparece; aberto some, porque a seção Responsáveis mostra
            o mesmo com mais espaço. */}
        {!isExpanded && (gestor?.nome || cessionario?.nome_completo) && (() => {
          const mesmaPessoa = !!gestor?.nome && !!cessionario?.nome_completo
            && gestor.nome.trim().toLowerCase() === cessionario.nome_completo.trim().toLowerCase();
          const soDigitos = (v?: string | null) => String(v ?? '').replace(/\D/g, '');
          const linhaCheia = [itemAtual.ddd, itemAtual.linha].filter(Boolean).join(' ');
          const tudo = [
            gestor?.nome, gestor?.cpf, gestor?.email, gestor?.telefone,
            mesmaPessoa ? null : cessionario?.nome_completo,
            mesmaPessoa ? null : cessionario?.cpf,
            linhaCheia || null,
          ].filter(Boolean).join(' | ');

          // Um dado = um botão de copiar. Sublinhado pontilhado é o que diz
          // "clique aqui" sem precisar de ícone em cada um.
          const Dado = ({ valor, copia }: { valor: string; copia?: string }) => (
            <CopyOnClick value={copia ?? valor} className="min-w-0 max-w-full">
              <span className="truncate text-[11px] font-semibold text-foreground underline decoration-dotted decoration-muted-foreground/40 underline-offset-[3px]">
                {valor}
              </span>
            </CopyOnClick>
          );

          return (
            <div className="mt-1 space-y-[3px] rounded-[8px] border border-border-warm bg-muted/30 px-2 py-1.5">
              {gestor?.nome && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="flex-shrink-0 rounded-[5px] bg-accent px-1.5 py-[1px] text-[8.5px] font-bold uppercase tracking-[0.7px] text-muted-foreground">
                    {mesmaPessoa ? 'Gestor · também cedente' : 'Gestor'}
                  </span>
                  <Dado valor={gestor.nome} />
                  {gestor.cpf && <Dado valor={gestor.cpf} copia={soDigitos(gestor.cpf)} />}
                  {gestor.email && <Dado valor={gestor.email} />}
                  {gestor.telefone && <Dado valor={gestor.telefone} copia={soDigitos(gestor.telefone)} />}
                </div>
              )}
              {!mesmaPessoa && cessionario?.nome_completo && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="flex-shrink-0 rounded-[5px] bg-accent px-1.5 py-[1px] text-[8.5px] font-bold uppercase tracking-[0.7px] text-muted-foreground">
                    Cedente
                  </span>
                  <Dado valor={cessionario.nome_completo} />
                  {cessionario.cpf && <Dado valor={cessionario.cpf} copia={soDigitos(cessionario.cpf)} />}
                  {cessionario.telefone && <Dado valor={cessionario.telefone} copia={soDigitos(cessionario.telefone)} />}
                </div>
              )}
              {(linhaCheia || itemAtual.tipo_chip || itemAtual.operadora_doadora) && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-[1px]">
                  {linhaCheia && (
                    <>
                      <span className="flex-shrink-0 text-[8.5px] font-bold uppercase tracking-[0.7px] text-muted-foreground/70">L1</span>
                      <Dado valor={linhaCheia} copia={soDigitos(linhaCheia)} />
                    </>
                  )}
                  {itemAtual.tipo_chip && produtoTemChip(itemAtual.produto_nome || itemAtual.produto?.nome) && (
                    <span className="text-[10px] text-muted-foreground">{rotuloTipoChip(itemAtual.tipo_chip)}</span>
                  )}
                  {itemAtual.operadora_doadora && (
                    <span className="text-[10px] text-muted-foreground">{itemAtual.operadora_doadora}</span>
                  )}
                  <CopyOnClick value={tudo} className="ml-auto flex-shrink-0">
                    <span className="text-[9.5px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">copiar tudo</span>
                  </CopyOnClick>
                </div>
              )}
            </div>
          );
        })()}

        {/* Barra de progresso */}
        <div className="mt-0.5">
          <ProgressoBadge
            camposObrigatorios={camposObrigatorios}
            camposPreenchidos={camposPreenchidos}
            isCancelado={itemAtual.coluna_atual === 'PEDIDO CANCELADO'}
            isAtivo={!!itemAtual.data_ativacao}
            foiRecuperado={!!itemAtual.foi_recuperado}
          />
        </div>

        {/* NOVO: Resumo compacto de obrigatoriedades */}
        {!compact && <ResumoObrigatorios
          item={{
            produto_nome: itemAtual.produto_nome || itemAtual.produto?.nome,
            familia: itemAtual.familia,
            quantidade: itemAtual.quantidade,
            ddd: itemAtual.ddd,
            linha: itemAtual.linha,
            tipo_chip: itemAtual.tipo_chip,
            operadora_doadora: itemAtual.operadora_doadora,
            linhas_telefone: itemAtual.linhas_telefone,
            endereco_instalacao: enderecoLocal || itemAtual.endereco_instalacao,
            gestor_id: itemAtual.gestor_id,
            cessionario_id: itemAtual.cessionario_id,
            sem_linha_fixa: itemAtual.sem_linha_fixa,
          }}
          gestor={gestor}
          cessionario={cessionario}
          expanded={isExpanded}
          onExpandClick={() => setIsExpanded(!isExpanded)}
        />}
      </CardHeader>

      {/* CardContent com Accordion - só aparece quando expandido */}
      {isExpanded && (
      <CardContent className="space-y-3 px-3 pb-3 pt-1">
        {/* Seções com Accordion */}
        <Accordion type="multiple" defaultValue={defaultExpandedSections} className="w-full">
          
          {/* Seção: Dados da Linha (PN/PP/TT/MP) */}
          {precisaLinhasTelefone && (
            <SecaoObrigatoria
              value="dados-linha"
              titulo="Dados da Linha"
              icone={Smartphone}
              camposObrigatorios={['DDD', 'Linha', 'Operadora Doadora', 'Tipo de Chip', 'Plano', 'Valor do Plano', 'Observações'].filter(c => camposObrigatorios.includes(c))}
              camposPreenchidos={camposPreenchidos}
            >
              <div className="space-y-4">
                {/*  Alerta de bloqueio pós-fechamento */}
                {dadosLinhaBloqueados && (
                  <Alert className="border-primary/50 bg-primary/5 dark:bg-primary/30">
                    <AlertDescription className="text-primary dark:text-primary text-sm flex items-center gap-2">
                       {temSnapshotRenovacao ? 'Comparativo da renovação fechado no snapshot do pedido. Faça uma revisão da proposta para alterar.': 'Dados da linha travados após fechamento do pedido. Alterações não são permitidas.'}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Indicador de salvamento */}
                {isSavingLinhas && (
                  <div className="mb-2 flex items-center gap-2 rounded-md bg-accent px-3 py-2 text-[11px] text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Salvando linhas...
                  </div>
                )}
                
                {/* Múltiplas Linhas Telefônicas */}
                <MultiLinhasField
                  ativacaoChip={produtoExigeAtivacaoChip(itemAtual.produto_nome || itemAtual.produto?.nome)}
                  semChip={!temChipProduto}
                  quantidade={itemAtual.quantidade || 1}
                  quantidadeDaProposta={!!(itemAtual as { linha_id?: string | null }).linha_id && /^(PN|TT)\b/i.test(String(itemAtual.produto_nome || itemAtual.produto?.nome || ''))}
                  isHA={isHA}
                  isFIBRA={isFIBRA}
                  disabled={dadosLinhaBloqueados}
                  semLinhaFixa={itemAtual.sem_linha_fixa || false}
                  onSemLinhaFixaChange={(value) => {
                    console.log('🌐 [FIBRA] Sem linha fixa:', value);
                    handleUpdateImmediate('sem_linha_fixa', value);
                  }}
                  linhas={(() => {
                    const linhasSalvas = itemAtual.linhas_telefone;
                    const qtd = quantidadeLinhasTelefone(itemAtual.quantidade || 1, linhasSalvas, isFIBRA);
                    
                    // Se tem linhas salvas, garantir que array tem tamanho correto
                    if (linhasSalvas && Array.isArray(linhasSalvas) && linhasSalvas.length > 0) {
                      const linhasCompletas = [...linhasSalvas];
                      
                      // Preencher slots vazios até quantidade esperada
                      while (linhasCompletas.length < qtd) {
                        linhasCompletas.push({ 
                          ddd: '', 
                          linha: '', 
                          operadora_doadora: null, 
                          tipo_chip: null 
                        });
                      }
                      
                      return linhasCompletas;
                    }
                    
                    // Retrocompatibilidade: migrar dados antigos
                    if (itemAtual.ddd && itemAtual.linha) {
                      const primeiraLinha = {
                        ddd: itemAtual.ddd,
                        linha: itemAtual.linha,
                        operadora_doadora: itemAtual.operadora_doadora,
                        tipo_chip: itemAtual.tipo_chip
                      };
                      
                      const linhasArray = [primeiraLinha];
                      
                      // Preencher slots vazios restantes
                      for (let i = 1; i < qtd; i++) {
                        linhasArray.push({ 
                          ddd: '', 
                          linha: '', 
                          operadora_doadora: null, 
                          tipo_chip: null 
                        });
                      }
                      
                      return linhasArray;
                    }
                    
                    // Caso totalmente vazio: criar array de acordo com quantidade
                    return Array.from({ length: qtd }, () => ({ 
                      ddd: '', 
                      linha: '', 
                      operadora_doadora: null, 
                      tipo_chip: null 
                    }));
                  })()}
                  onQuantidadeChange={(qtd) => handleUpdateImmediate('quantidade', qtd)}
                  onLinhasChange={async (linhas) => {
                    console.log('💾 [ATOMIC] Enfileirando salvamento de linhas:', linhas);
                    setIsSavingLinhas(true);
                    
                    try {
                      const primeiraLinha = linhas[0] as any || {};
                      
                      // ✅ UPDATE ATÔMICO via fila (1 request única)
                      await pendingSaves.enqueue(itemAtual.id, {
                        linhas_telefone: linhas,
                        ddd: primeiraLinha?.ddd || null,
                        linha: primeiraLinha?.linha || null,
                        operadora_doadora: primeiraLinha?.operadora_doadora || null,
                        tipo_chip: primeiraLinha?.tipo_chip || null,
                      });
                      
                      toast.success('Linhas salvas com sucesso');
                    } catch (error) {
                      console.error('❌ Erro ao salvar linhas:', error);
                      toast.error('Erro ao salvar linhas. Tente novamente.');
                    } finally {
                      setIsSavingLinhas(false);
                    }
                  }}
                />

                {/* Observações - dentro da seção Dados da Linha */}
                <CampoObrigatorioInline
                  label="Observações"
                  value={itemAtual.observacoes_item}
                  onChange={(v) => handleUpdate('observacoes_item', v)}
                  onBlur={(v) => handleUpdateImmediate('observacoes_item', v)}
                  type="textarea"
                  placeholder="Digite observações importantes sobre este item..."
                  obrigatorio={camposObrigatorios.includes('Observações')}
                  minLength={10}
                  disabled={dadosLinhaBloqueados}
                />
              </div>
            </SecaoObrigatoria>
          )}


          {/* Seção: Responsáveis */}
          {camposObrigatorios.includes('Gestor') && (() => {
            console.log('🎨 RENDERIZANDO SEÇÃO RESPONSÁVEIS:', {
              itemId: itemAtual.id,
              clienteId: pedido.cliente_id,
              gestorId: itemAtual.gestor_id,
              camposObrigatorios: camposObrigatorios.filter(c => ['Gestor', 'Cessionário'].includes(c)),
              timestamp: new Date().toISOString()
            });
            return null;
          })()}
          {camposObrigatorios.includes('Gestor') && (
            <SecaoObrigatoria
              value="responsaveis"
              titulo="Responsáveis"
              icone={Users}
              camposObrigatorios={['Gestor', 'Cessionário'].filter(c => camposObrigatorios.includes(c))}
              camposPreenchidos={camposPreenchidos}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Coluna 1: Gestor */}
                <div className="space-y-4">
                  {pedido?.cliente_id ? (
                    <GestorField
                      config={{
                        obrigatoriedades_catalogo: {
                          codigo: 'gestor_cliente',
                          nome: 'Gestor',
                          tipo_dado: 'select',
                          campo_tabela: 'gestor_id',
                          id: ''
                        },
                        mensagem_erro: 'Selecione o gestor responsável no cliente',
                        mensagem_ajuda: 'Gestor que será responsável por este item',
                        id: '',
                        validacao_regex: null,
                        valor_default: null,
                        minimo: null,
                        maximo: null,
                        permite_multiplos: false,
                        obrigatorio_em_negociacao: false,
                        obrigatorio_em_pedido_fechado: true,
                        obrigatorio_em_rascunho: false
                      }}
                      value={itemAtual.gestor_id || null}
                      onChange={async (id) => {
                        console.log('🎯 [GESTOR] onChange disparado:', {
                          gestorId: id,
                          itemId: itemAtual.id,
                          valorAnterior: itemAtual.gestor_id,
                          timestamp: new Date().toISOString()
                        });
                        
                        try {
                          await handleUpdateImmediate('gestor_id', id);
                          
                          // ✅ FORÇAR refetch imediato após salvamento
                          console.log('🔄 [GESTOR] Forçando refetch do item...');
                          await queryClient.refetchQueries({ 
                            queryKey: ['item-completo', itemAtual.id] 
                          });
                          
                          console.log('✅ [GESTOR] Refetch concluído com sucesso');
                        } catch (error) {
                          console.error('❌ [GESTOR] Erro ao salvar:', error);
                          toast.error('Não salvou o gestor. Atualize a tela e tente de novo.');
                        }
                      }}
                      clienteId={pedido.cliente_id}
                      erro={!itemAtual.gestor_id ? 'Campo obrigatório' : undefined}
                      obrigatorio={true}
                    />
                  ) : (
                    <Alert variant="destructive">
                      <AlertDescription>
                         Aguardando identificação do cliente
                      </AlertDescription>
                    </Alert>
                  )}

                  {gestor?.nome && (
                    <GestorCessionarioBadge
                      tipo="gestor"
                      nome={gestor.nome}
                      telefone={gestor.telefone}
                      whatsapp={gestor.whatsapp}
                      email={gestor.email}
                      rg={gestor.rg}
                      cpf={gestor.cpf}
                      variant="columns"
                      onEdit={() => setEditandoGestor(true)}
                    />
                  )}
                </div>

                {/* Coluna 2: Cessionário */}
                <div className="space-y-4">
                  {pedido?.cliente_id ? (
                    <CessionarioField
                      config={{
                        obrigatoriedades_catalogo: {
                          codigo: 'cessionario',
                          nome: 'Cessionário',
                          tipo_dado: 'select',
                          campo_tabela: 'cessionario_id',
                          id: ''
                        },
                        mensagem_erro: 'Selecione o cessionário responsável',
                        mensagem_ajuda: 'Cessionário que será responsável por este item',
                        id: '',
                        validacao_regex: null,
                        valor_default: null,
                        minimo: null,
                        maximo: null,
                        permite_multiplos: false,
                        obrigatorio_em_negociacao: false,
                        obrigatorio_em_pedido_fechado: true,
                        obrigatorio_em_rascunho: false
                      }}
                      value={itemAtual.cessionario_id || null}
                      onChange={(id) => handleUpdateImmediate('cessionario_id', id)}
                      clienteId={pedido.cliente_id}
                      erro={!itemAtual.cessionario_id && camposObrigatorios.includes('Cessionário') ? 'Campo obrigatório' : undefined}
                      obrigatorio={camposObrigatorios.includes('Cessionário')}
                      gestorData={gestor ? {
                        id: gestor.id,
                        nome: gestor.nome,
                        telefone: gestor.telefone,
                        whatsapp: gestor.whatsapp,
                        email: gestor.email,
                        cpf: gestor.cpf,
                        rg: gestor.rg
                      } : null}
                      onCompletarGestor={gestor ? () => setEditandoGestor(true) : undefined}
                    />
                  ) : (
                    <Alert variant="destructive">
                      <AlertDescription>
                         Aguardando identificação do cliente
                      </AlertDescription>
                    </Alert>
                  )}

                  {cessionario?.nome_completo && (
                  <GestorCessionarioBadge
                    tipo="cessionario"
                    nome={cessionario.nome_completo}
                    telefone={cessionario.telefone}
                    whatsapp={cessionario.whatsapp}
                    email={cessionario.email}
                    rg={cessionario.rg}
                    cpf={cessionario.cpf}
                    variant="columns"
                    onEdit={() => setEditandoCessionario(true)}
                  />
                  )}
                </div>
              </div>
            </SecaoObrigatoria>
          )}

          {/* Seção: Dados da Operadora */}
          <SecaoObrigatoria
            value="operadora"
            titulo="Dados da Operadora"
            icone={Building2}
            camposObrigatorios={[]}
            camposPreenchidos={[]}
          >
            <div className="grid grid-cols-3 gap-4">
              <CampoObrigatorioInline
                label="HP"
                value={itemAtual.numero_simulacao_operadora}
                onChange={(v) => handleUpdate('numero_simulacao_operadora', v)}
                onBlur={(v) => handleUpdateImmediate('numero_simulacao_operadora', v)}
              />
              <CampoObrigatorioInline
                label="Cotação"
                value={itemAtual.numero_cotacao_operadora}
                onChange={(v) => handleUpdate('numero_cotacao_operadora', v)}
                onBlur={(v) => handleUpdateImmediate('numero_cotacao_operadora', v)}
              />
              <CampoObrigatorioInline
                label="Proposta"
                value={itemAtual.numero_pedido_operadora}
                onChange={(v) => handleUpdate('numero_pedido_operadora', v)}
                onBlur={(v) => handleUpdateImmediate('numero_pedido_operadora', v)}
              />
            </div>
          </SecaoObrigatoria>

          {/* Seção: Datas */}
          <SecaoObrigatoria
            value="datas"
            titulo="Datas Importantes"
            icone={Calendar}
            camposObrigatorios={[]}
            camposPreenchidos={[]}
          >
            <div className="grid grid-cols-2 gap-4">
              <CampoObrigatorioInline
                label="Mês de Trabalho"
                value={itemAtual.mes_trabalho}
                onChange={(v) => handleUpdateImmediate('mes_trabalho', v)}
                type="month"
              />
              <CampoObrigatorioInline
                label="Data de Ativação"
                value={itemAtual.data_ativacao}
                onChange={(v) => handleUpdateImmediate('data_ativacao', v)}
                type="date"
              />
            </div>
          </SecaoObrigatoria>

          {/* Seção: Agendas */}
          <SecaoObrigatoria
            value="agendas"
            titulo="Agendas"
            icone={CalendarCheck}
            camposObrigatorios={['Data Agendamento', 'Tipo de Agenda'].filter(c => camposObrigatorios.includes(c))}
            camposPreenchidos={camposPreenchidos}
          >
            <div className="space-y-4">
              {/* Linha 1: Data Agendamento + Tipo de Agenda */}
              <div className="grid grid-cols-2 gap-4">
                {agendaFixaTravada ? (
                  <AgendaFixaTravada itemId={itemAtual.id} quadroAtual={quadroAtual} value={itemAtual.data_agendamento} />
                ) : <>
                  {agendaPorTurno ? (
                    <AgendaTurnoInline
                      value={itemAtual.data_agendamento}
                      obrigatorio={camposObrigatorios.includes('Data Agendamento')}
                      onSalvar={async (valor) => {
                        await handleUpdateImmediate('data_agendamento', valor);
                        // Fibra é sempre instalação — fixa o tipo junto com a data
                        if (itemAtual.agenda_tipo !== 'INSTALACAO') {
                          await handleUpdateImmediate('agenda_tipo', 'INSTALACAO');
                        }
                      }}
                    />
                  ) : (
                    <CampoObrigatorioInline
                      label="Data Agendamento"
                      value={agendaEmSaoPaulo ? `${agendaEmSaoPaulo.data}T${agendaEmSaoPaulo.hora}` : ''}
                      onChange={(v) => {
                        const valor = v ? v + ':00-03:00' : null;
                        handleUpdateImmediate('data_agendamento', valor);
                      }}
                      type="datetime-local"
                      obrigatorio={camposObrigatorios.includes('Data Agendamento')}
                    />
                  )}
                  
                  <CampoObrigatorioInline
                    label="Tipo de Agenda"
                    value={itemAtual.agenda_tipo || 'NENHUMA'}
                    onChange={(v) => handleUpdateImmediate('agenda_tipo', v === 'NENHUMA' ? null : v)}
                    type="select"
                    options={[
                      { value: 'NENHUMA', label: '⚪ Sem agenda' },
                      { value: 'PORTABILIDADE', label: 'Portabilidade'},
                      { value: 'INSTALACAO', label: 'Instalação'}
                    ]}
                    obrigatorio={camposObrigatorios.includes('Tipo de Agenda')}
                  />
                </>}
              </div>


              {/* Linha 3: Observação da Agenda/Portabilidade */}
              {itemAtual.agenda_tipo && itemAtual.agenda_tipo !== 'NENHUMA' && (
                <CampoObrigatorioInline
                  label={`Observação da ${itemAtual.agenda_tipo === 'PORTABILIDADE' ? 'Portabilidade' : 'Instalação'}`}
                  value={itemAtual.agenda_observacoes || ''}
                  onChange={(v) => handleUpdate('agenda_observacoes', v)}
                  onBlur={(v) => handleUpdateImmediate('agenda_observacoes', v)}
                  type="textarea"
                  placeholder={`Detalhes sobre a ${itemAtual.agenda_tipo.toLowerCase()}...`}
                />
              )}

              {/* Badge informativo quando há agendamento */}
              {!agendaFixaTravada && itemAtual.agenda_tipo && itemAtual.agenda_tipo !== 'NENHUMA' && itemAtual.data_agendamento && (
                <Alert className="border-primary/30 bg-primary/5">
                  <Calendar className="h-4 w-4 text-primary" />
                  <AlertDescription className="text-sm text-foreground">
                    {itemAtual.agenda_tipo === 'PORTABILIDADE'? '': ''}
                    {' '}Agendado para {ehInstalacao(itemAtual.agenda_tipo)
                      ? agendaInstalacaoTexto(itemAtual.data_agendamento, { ano: true })
                      : format(toBrasilia(new Date(itemAtual.data_agendamento)), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </SecaoObrigatoria>

          {/* Seção: Endereço de Instalação */}
          <SecaoObrigatoria
            value="endereco"
            titulo="Endereço de Instalação"
            icone={MapPin}
            camposObrigatorios={['Endereço de Instalação'].filter(c => camposObrigatorios.includes(c))}
            camposPreenchidos={camposPreenchidos}
          >
            <EnderecoInstalacaoInline
              endereco={enderecoLocal || itemAtual.endereco_instalacao || null}
              onChange={(endereco) => {
                console.log('📍 [DEBOUNCE] Atualizando endereço local:', endereco);
                setEnderecoLocal(endereco);
                
                // Limpar debounce anterior
                if (enderecoDebounceRef.current) {
                  clearTimeout(enderecoDebounceRef.current);
                }
                
                // ✅ AJUSTE 6: Endereço usa enqueue (mesmo mecanismo, com debounce)
                enderecoDebounceRef.current = setTimeout(async () => {
                  try {
                    await pendingSaves.enqueue(itemAtual.id, {
                      endereco_instalacao: endereco
                    });
                    console.log('✅ Endereço salvo via enqueue');
                  } catch (err) {
                    console.error('❌ Erro ao salvar endereço:', err);
                    const motivo = String((err as { message?: string })?.message || '').replace(/^ITEM_EM_TRAMITACAO:\s*/, '');
                    toast.error(motivo ? `Não salvou o endereço: ${motivo}` : 'Não salvou o endereço. Confira os campos e tente de novo.');
                  }
                }, 1000); // 1 segundo de debounce
              }}
              onBlur={handleEnderecoBlur}
              obrigatorio={camposObrigatorios.includes('Endereço de Instalação')}
            />
          </SecaoObrigatoria>

          {/* Seção: Contrato PF da fibra — TT Fixa (Douglas 22/10/2026): sem ele não sai o e-mail à gerente de contas */}
          {camposObrigatorios.includes('Contrato PF da fibra') && (
            <SecaoObrigatoria
              value="contrato-pf"
              titulo="Contrato PF da fibra"
              icone={FileText}
              camposObrigatorios={['Contrato PF da fibra']}
              camposPreenchidos={camposPreenchidos}
            >
              <div className="grid gap-1">
                <Input
                  key={itemAtual.tt_contrato_pf || ''}
                  defaultValue={itemAtual.tt_contrato_pf || ''}
                  placeholder="Número do contrato da fibra no nome da pessoa física"
                  className="h-9 text-sm"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (itemAtual.tt_contrato_pf || '')) void handleUpdateImmediate('tt_contrato_pf', v || null);
                  }}
                />
                <p className="text-[11px] text-muted-foreground">Está na conta da fibra do cliente pessoa física. A gerente de contas precisa dele para migrar a fibra para o CNPJ.</p>
              </div>
            </SecaoObrigatoria>
          )}

          {/* Seção: Observações - apenas para produtos que NÃO precisam de linhas telefônicas */}
          {!precisaLinhasTelefone && (
            <SecaoObrigatoria
              value="observacoes"
              titulo="Observações"
              icone={PenLine}
              camposObrigatorios={['Observações'].filter(c => camposObrigatorios.includes(c))}
              camposPreenchidos={camposPreenchidos}
            >
              <CampoObrigatorioInline
                label="Observações"
                value={itemAtual.observacoes_item}
                onChange={(v) => handleUpdate('observacoes_item', v)}
                onBlur={(v) => handleUpdateImmediate('observacoes_item', v)}
                type="textarea"
                placeholder="Digite observações importantes sobre este item..."
                obrigatorio={camposObrigatorios.includes('Observações')}
                minLength={10}
              />
            </SecaoObrigatoria>
          )}
        </Accordion>
      </CardContent>
      )}

      {/* Seção: Cancelamento e Recuperação */}
      {itemAtual.data_cancelamento && (
        <div className="px-6 pb-6">
          <CancelamentoRecuperacaoCard item={itemAtual} />
        </div>
      )}

      {/* Dialog de edição do Gestor */}
      <GestorCessionarioEditDialog
        open={editandoGestor}
        onOpenChange={setEditandoGestor}
        tipo="gestor"
        dadosIniciais={gestor ? {
          nome: gestor.nome || '',
          telefone: gestor.telefone || '',
          whatsapp: gestor.whatsapp || '',
          email: gestor.email || '',
          cpf: gestor.cpf || '',
          rg: gestor.rg || ''
        } : null}
        onSave={async (dados) => {
          if (gestor?.id) {
            try {
              await updateGestor({
                id: gestor.id,
                data: {
                  nome: dados.nome,
                  telefone: dados.telefone,
                  whatsapp: dados.whatsapp,
                  email: dados.email,
                  cpf: dados.cpf,
                  rg: dados.rg
                }
              });
              
              // ✅ NOVO: Buscar cessionários vinculados a este gestor e sincronizar
              const { data: cessionariosVinculados } = await supabase
                .from('cliente_cessionario')
                .select('id')
                .eq('originado_do_gestor_id', gestor.id);
              
              if (cessionariosVinculados && cessionariosVinculados.length > 0) {
                // Atualizar todos os cessionários vinculados com os novos dados do gestor
                await supabase
                  .from('cliente_cessionario')
                  .update({
                    nome_completo: dados.nome,
                    telefone: dados.telefone,
                    whatsapp: dados.whatsapp,
                    email: dados.email,
                    cpf: dados.cpf,
                    rg: dados.rg,
                    updated_at: new Date().toISOString(),
                  })
                  .eq('originado_do_gestor_id', gestor.id);
                
                // Invalidar cache dos cessionários atualizados
                for (const cess of cessionariosVinculados) {
                  await queryClient.invalidateQueries({ 
                    queryKey: ['cessionario-completo', cess.id, pedido.cliente_id] 
                  });
                }
                await queryClient.invalidateQueries({ 
                  queryKey: ['cessionario-completo'], exact: false 
                });
                await queryClient.invalidateQueries({ 
                  queryKey: ['cliente-cessionarios', pedido.cliente_id] 
                });
                
                toast.success(`Gestor e ${cessionariosVinculados.length} cedente(s) vinculado(s) atualizados!`);
              } else {
                toast.success("Gestor atualizado com sucesso!");
              }
              
              // ✅ Invalidar e refetch com queryKey CORRETA (gestor-completo)
              await queryClient.invalidateQueries({ 
                queryKey: ['gestor-completo', gestor.id, pedido.cliente_id] 
              });
              await queryClient.refetchQueries({ 
                queryKey: ['gestor-completo', gestor.id, pedido.cliente_id] 
              });
              // ✅ Só fecha modal após refetch confirmar sucesso
              setEditandoGestor(false);
            } catch (error) {
              toast.error("Erro ao atualizar gestor", {
                description: "Tente novamente"
              });
            }
          }
        }}
      />

      {/* Dialog de edição do Cessionário */}
      <GestorCessionarioEditDialog
        open={editandoCessionario}
        onOpenChange={setEditandoCessionario}
        tipo="cessionario"
        dadosIniciais={cessionario ? {
          nome: cessionario.nome_completo || '',
          telefone: cessionario.telefone || '',
          whatsapp: cessionario.whatsapp || '',
          email: cessionario.email || '',
          cpf: cessionario.cpf || '',
          rg: cessionario.rg || ''
        } : null}
        onSave={async (dados) => {
          if (cessionario?.id) {
            try {
              await updateCessionario({
                id: cessionario.id,
                data: {
                  nome_completo: dados.nome,
                  telefone: dados.telefone,
                  whatsapp: dados.whatsapp,
                  email: dados.email,
                  cpf: dados.cpf,
                  rg: dados.rg
                }
              });
              // ✅ Invalidar e refetch com queryKey CORRETA (cessionario-completo)
              await queryClient.invalidateQueries({ 
                queryKey: ['cessionario-completo', cessionario.id, pedido.cliente_id] 
              });
              await queryClient.refetchQueries({ 
                queryKey: ['cessionario-completo', cessionario.id, pedido.cliente_id] 
              });
              // ✅ Só fecha modal após refetch confirmar sucesso
              setEditandoCessionario(false);
            } catch (error) {
              toast.error("Erro ao atualizar cessionário", {
                description: "Tente novamente"
              });
            }
          }
        }}
      />

      {/* Botão de ação BKO individual - aparece abaixo de cada item do CONTRATO_PRIME ou TRAMITACAO_VIVO */}
      {(quadroAtual === 'CONTRATO_PRIME' || quadroAtual === 'TRAMITACAO_VIVO') && (
        <div className={cn('px-3 pb-2', isExpanded && 'pt-1')}>
          <BotaoAcaoBKO 
            item={{
              id: itemAtual.id,
              pedido_id: itemAtual.pedido_id,
              coluna_atual: colunaAtual,
              quadro_atual: quadroAtual,
              produto_nome: itemAtual.produto_nome || itemAtual.produto?.nome || 'Produto',
              quantidade: itemAtual.quantidade || 1,
              subtotal: itemAtual.subtotal || itemAtual.valor_unit || 0,
              familia: itemAtual.familia || '',
              plano_nome: itemAtual.plano_nome || '',
              bko_criacao_id: itemAtual.bko_criacao_id,
              bko_aceite_id: itemAtual.bko_aceite_id,
              bko_input_id: itemAtual.bko_input_id,
              bko_tramitacao_id: itemAtual.bko_tramitacao_id,
            }}
            pedidoId={itemAtual.pedido_id}
            todosItens={todosItens.map(i => ({
              id: i.id,
              pedido_id: i.pedido_id,
              coluna_atual: extractValue(i.coluna_atual) || i.coluna_atual,
              quadro_atual: extractValue(i.quadro_atual) || i.quadro_atual,
              produto_nome: i.produto_nome || i.produto?.nome || 'Produto',
              quantidade: i.quantidade || 1,
              subtotal: i.subtotal || i.valor_unit || 0,
              familia: i.familia || '',
              plano_nome: i.plano_nome || '',
              bko_criacao_id: i.bko_criacao_id,
              bko_aceite_id: i.bko_aceite_id,
              bko_input_id: i.bko_input_id,
              bko_tramitacao_id: i.bko_tramitacao_id,
            }))}
            showDescription={isExpanded}
            showTriagemHint={isExpanded}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['item-completo', itemAtual.id] });
              queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', pedido?.id] });
            }}
          />
        </div>
      )}
    </Card>
  );
});
